import { useEffect, useRef, useState } from "react";
import { api, API, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Clipboard, Save, X, Camera, Loader2, Trash2, Pencil } from "lucide-react";
import { toast } from "sonner";

const empty = { diagnosis: "", cause: "", solution: "", mechanic_name: "" };

function hasContent(order) {
  const tr = order?.tech_report || {};
  return (tr.diagnosis || "").trim() || (tr.cause || "").trim() || (tr.solution || "").trim()
    || (tr.mechanic_name || "").trim() || (order?.laudo_photos || []).length > 0;
}

export function LaudoSection({ order, setOrder, readOnly }) {
  const tr = order?.tech_report || {};
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(empty);
  const [saving, setSaving] = useState(false);
  const [mechs, setMechs] = useState([]);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    if (!editing) return;
    api.get("/erp/mechanics").then(({ data }) => setMechs(data || [])).catch(() => setMechs([]));
  }, [editing]);

  const open = () => {
    setDraft({ diagnosis: tr.diagnosis || "", cause: tr.cause || "", solution: tr.solution || "", mechanic_name: tr.mechanic_name || order?.mechanic_name || "" });
    setEditing(true);
  };
  const cancel = () => { setEditing(false); setDraft(empty); };

  const save = async () => {
    setSaving(true);
    try {
      const payload = { tech_report: { diagnosis: draft.diagnosis || "", cause: draft.cause || "", solution: draft.solution || "", mechanic_name: draft.mechanic_name || null } };
      const { data } = await api.patch(`/orders/${order.id}`, payload);
      setOrder((prev) => ({ ...(prev || data), tech_report: data.tech_report }));
      toast.success("Laudo salvo");
      setEditing(false);
    } catch (e) { toast.error(formatApiError(e)); } finally { setSaving(false); }
  };

  const addPhoto = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post(`/orders/${order.id}/photos?category=laudo`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setOrder((prev) => prev ? { ...prev, laudo_photos: [...(prev.laudo_photos || []), data] } : prev);
      toast.success("Foto do laudo enviada");
    } catch (e) { toast.error(formatApiError(e)); } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const removePhoto = async (pid) => {
    try {
      await api.delete(`/orders/${order.id}/photos/${pid}`);
      setOrder((prev) => prev ? { ...prev, laudo_photos: (prev.laudo_photos || []).filter((p) => p.id !== pid) } : prev);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const show = editing || hasContent(order);
  if (!show) {
    if (readOnly) return null;
    return (
      <div className="mt-5" data-testid="laudo-empty">
        <Button data-testid="laudo-add" type="button" variant="outline" size="sm" onClick={open} className="bg-zinc-950 border-zinc-800 text-zinc-200">
          <Clipboard className="h-4 w-4 mr-1" /> Adicionar laudo técnico
        </Button>
      </div>
    );
  }

  const Field = ({ k, label, placeholder }) => (
    <div>
      <div className="text-[11px] font-mono-caps text-zinc-500 mb-1">{label}</div>
      <Textarea data-testid={`laudo-${k}`} value={draft[k]} onChange={(e) => setDraft((p) => ({ ...p, [k]: e.target.value }))}
        className="bg-zinc-950 border-zinc-800 text-zinc-100 min-h-[72px]" placeholder={placeholder} />
    </div>
  );

  const nameOptions = [...new Set([...(mechs || []).filter((m) => m.active !== false).map((m) => m.name), ...(draft.mechanic_name ? [draft.mechanic_name] : [])])];

  return (
    <section data-testid="laudo-section" className="mt-5 rounded-xl border border-card2-border bg-appbg-soft/40 p-4">
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <Clipboard className="h-4 w-4 text-amber-brand" />
          <h3 className="font-display text-base font-bold uppercase text-zinc-100">Laudo técnico</h3>
        </div>
        {!readOnly && !editing && (
          <Button data-testid="laudo-edit" type="button" variant="outline" size="sm" onClick={open} className="h-8 bg-zinc-950 border-zinc-800 text-zinc-200">
            <Pencil className="h-3.5 w-3.5 mr-1" /> Editar
          </Button>
        )}
      </div>

      {editing ? (
        <div className="mt-3 space-y-3">
          <Field k="diagnosis" label="Diagnóstico (o que foi encontrado)" placeholder="Ex.: bobina de ignição do cilindro 3 em curto; falha intermitente a frio" />
          <Field k="cause" label="Causa provável" placeholder="Ex.: desgaste natural (veículo com 120.000 km) e infiltração de água" />
          <Field k="solution" label="Solução recomendada" placeholder="Ex.: substituição das 4 bobinas e das velas, limpeza dos conectores" />
          <div>
            <div className="text-[11px] font-mono-caps text-zinc-500 mb-1">Mecânico que fez o laudo</div>
            <Select value={draft.mechanic_name || "__none__"} onValueChange={(v) => setDraft((p) => ({ ...p, mechanic_name: v === "__none__" ? "" : v }))}>
              <SelectTrigger data-testid="laudo-mechanic" className="bg-zinc-900 border-zinc-800"><SelectValue placeholder="Selecione" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__none__">Nenhum</SelectItem>
                {nameOptions.map((n) => <SelectItem key={n} value={n} data-testid={`laudo-mechanic-opt-${n}`}>{n}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="flex gap-2">
            <Button data-testid="laudo-save" onClick={save} disabled={saving} className="bg-green-brand hover:bg-green-brandDark text-white font-bold">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4 mr-1" />} Salvar laudo
            </Button>
            <Button data-testid="laudo-cancel" variant="outline" onClick={cancel} className="bg-zinc-950 border-zinc-800">
              <X className="h-4 w-4 mr-1" /> Cancelar
            </Button>
          </div>
        </div>
      ) : (
        <dl className="mt-3 space-y-2 text-sm">
          {[["Diagnóstico", tr.diagnosis], ["Causa provável", tr.cause], ["Solução recomendada", tr.solution]].map(([lbl, v]) => (
            v && v.trim() ? (
              <div key={lbl} data-testid={`laudo-view-${lbl}`} className="rounded-md border border-zinc-800 bg-zinc-950/40 p-2.5">
                <dt className="text-[11px] font-mono-caps text-zinc-500">{lbl}</dt>
                <dd className="whitespace-pre-wrap text-zinc-100">{v}</dd>
              </div>
            ) : null
          ))}
          {tr.mechanic_name && (
            <div className="text-xs text-zinc-400">Mecânico (laudo): <span className="font-semibold text-zinc-200">{tr.mechanic_name}</span>{tr.updated_at ? <span className="text-zinc-600"> · {new Date(tr.updated_at).toLocaleString("pt-BR")}</span> : null}</div>
          )}
        </dl>
      )}

      {!readOnly && (
        <div className="mt-3">
          <input ref={fileRef} type="file" accept="image/*" hidden multiple onChange={(e) => { for (const f of e.target.files || []) addPhoto(f); }} data-testid="laudo-photo-input" />
          <Button type="button" variant="outline" size="sm" onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="laudo-photo-btn" className="bg-zinc-950 border-zinc-800">
            {uploading ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Camera className="h-4 w-4 mr-1" />} Fotos do laudo
          </Button>
        </div>
      )}

      {(order.laudo_photos || []).length > 0 && (
        <div className="mt-3 grid grid-cols-3 sm:grid-cols-4 gap-2" data-testid="laudo-photos">
          {order.laudo_photos.map((p) => (
            <div key={p.id} className="relative group">
              <img src={`${API}/files/${p.id}`} alt="foto do laudo" className="aspect-square w-full object-cover rounded-lg border border-zinc-800" loading="lazy" />
              {!readOnly && (
                <button type="button" onClick={() => removePhoto(p.id)} data-testid={`laudo-photo-remove-${p.id}`}
                  className="absolute top-1 right-1 h-7 w-7 rounded-full bg-black/70 text-red-300 opacity-0 group-hover:opacity-100 flex items-center justify-center">
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              )}
            </div>
          ))}
        </div>
      )}
    </section>
  );
}
